import {
  Computer,
  SportsEsports,
  Vrpano,
  SportsTennis,
  Headphones,
  Chair,
  Category,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { InventoryCategory, BookingStatus } from '@lov/shared';

// ============================================
// TYPES
// ============================================

export interface EquipmentCategoryConfig {
  icon: SvgIconComponent;
  label: string;
}

export interface StatusConfig {
  color: string;
  bg: string;
  border: string;
}

export interface EquipmentItem {
  inventoryItemId: string;
  venueInventoryId: string;
  name: string;
  category: InventoryCategory;
  quantity: number;
  pricePerUnit: number;
}

// ============================================
// CONFIGURATION
// ============================================

/**
 * Equipment category icon and label configuration
 * Icons only - no category colors (scalable design)
 */
export const EQUIPMENT_CATEGORY_CONFIG: Record<InventoryCategory, EquipmentCategoryConfig> = {
  GAMING_PC: { icon: Computer, label: 'Gaming PC' },
  GAMING_CONSOLE: { icon: SportsEsports, label: 'Console' },
  VR_EQUIPMENT: { icon: Vrpano, label: 'VR' },
  SPORTS_EQUIPMENT: { icon: SportsTennis, label: 'Sports' },
  AUDIO_EQUIPMENT: { icon: Headphones, label: 'Audio' },
  FURNITURE: { icon: Chair, label: 'Furniture' },
  OTHER: { icon: Category, label: 'Other' },
};

/**
 * Booking status colors (6 statuses including CHECKED_IN)
 * Used for left border and subtle backgrounds
 */
export const STATUS_CONFIG: Record<BookingStatus, StatusConfig> = {
  CONFIRMED: { color: 'emerald', bg: 'white', border: '#10b981' },
  PENDING: { color: 'amber', bg: '#fffbeb', border: '#f59e0b' },
  COMPLETED: { color: 'sky', bg: 'white', border: '#0ea5e9' },
  CANCELLED: { color: 'rose', bg: '#fff1f2', border: '#f87171' },
  NO_SHOW: { color: 'slate', bg: '#f8fafc', border: '#94a3b8' },
  CHECKED_IN: { color: 'blue', bg: '#eff6ff', border: '#3b82f6' },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get icon components for an array of equipment items
 * Returns unique category icons (deduplicated)
 */
export function getEquipmentIcons(items: EquipmentItem[]): SvgIconComponent[] {
  const uniqueCategories = getUniqueCategories(items);
  return uniqueCategories.map((category) => EQUIPMENT_CATEGORY_CONFIG[category].icon);
}

/**
 * Get unique equipment categories from items
 */
export function getUniqueCategories(items: EquipmentItem[]): InventoryCategory[] {
  const categories = items.map((item) => item.category);
  return Array.from(new Set(categories));
}

/**
 * Get primary (most common) category from equipment items
 * Used to determine dominant equipment type for display
 */
export function getPrimaryCategory(items: EquipmentItem[]): InventoryCategory | null {
  if (items.length === 0) return null;

  // Count occurrences of each category
  const categoryCounts: Partial<Record<InventoryCategory, number>> = {};
  items.forEach((item) => {
    categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
  });

  // Find category with highest count
  let maxCount = 0;
  let primaryCategory: InventoryCategory | null = null;

  (Object.entries(categoryCounts) as [InventoryCategory, number][]).forEach(([category, count]) => {
    if (count > maxCount) {
      maxCount = count;
      primaryCategory = category;
    }
  });

  return primaryCategory;
}

/**
 * Get status configuration for a booking status
 */
export function getStatusConfig(status: BookingStatus): StatusConfig {
  return STATUS_CONFIG[status];
}

/**
 * Get category configuration for an equipment category
 */
export function getCategoryConfig(category: InventoryCategory): EquipmentCategoryConfig {
  return EQUIPMENT_CATEGORY_CONFIG[category];
}

/**
 * Format equipment list for compact display
 * e.g., "Gaming PC x2, Console x1" or "PC x2, Console x1, +2 more"
 */
export function formatEquipmentList(items: EquipmentItem[], maxItems = 2): string {
  if (items.length === 0) return 'No equipment';

  // Group by category
  const categoryGroups: Partial<Record<InventoryCategory, number>> = {};
  items.forEach((item) => {
    categoryGroups[item.category] = (categoryGroups[item.category] || 0) + item.quantity;
  });

  const entries = Object.entries(categoryGroups) as [InventoryCategory, number][];

  if (entries.length <= maxItems) {
    return entries
      .map(([category, qty]) => `${EQUIPMENT_CATEGORY_CONFIG[category].label} x${qty}`)
      .join(', ');
  }

  const displayed = entries.slice(0, maxItems);
  const remaining = entries.length - maxItems;

  return (
    displayed
      .map(([category, qty]) => `${EQUIPMENT_CATEGORY_CONFIG[category].label} x${qty}`)
      .join(', ') + `, +${remaining} more`
  );
}
